/**
 * Profile holdings for the Positions tab, scoped to the viewer.
 */

import {
    isServer,
    useInfiniteQuery,
    useQuery,
    type InfiniteData,
    type UseInfiniteQueryResult,
    type UseQueryResult,
} from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type {
    SocialProfileEventPosition,
    SocialProfilePositionsResponse,
    SocialProfilePositionsState,
} from 'models/socialProfilePositions';
import config from 'utils/config';

import { useSocialProfile } from './socialProfile';
import type { SurfaceState } from './socialProfileVisibility';
import type { SocialViewerScope } from './socialViewerScope';
import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const DEFAULT_PAGE_SIZE = 20;
const AVAILABILITY_PROBE_LIMIT = 1;
const STALE_TIME = 30 * 1000;
const AVAILABILITY_STALE_TIME = 60 * 1000;
const GC_TIME = 5 * 60 * 1000;

export type SocialProfileHoldingsFilter = 'open' | 'closed';

/** Viewer scope plus id for authed endpoints. */
export interface SocialProfileSurfaceScope {
    scope: SocialViewerScope;
    viewerUserId?: string;
}

export interface SocialProfileHoldingsPage {
    holdings: SocialProfileEventPosition[];
    cursor: string | undefined;
    state: SocialProfilePositionsState;
}

/**
 * Resolve the profile surface route; missing viewer id means public.
 */
export function resolveSocialProfileSurfaceScope({
    nickname,
    ownNickname,
    isLoggedIn,
    viewerUserId,
}: {
    nickname: string;
    ownNickname: string | undefined;
    isLoggedIn: boolean;
    viewerUserId: string | null | undefined;
}): SocialProfileSurfaceScope {
    if (!isLoggedIn || !viewerUserId) {
        return { scope: 'public' };
    }
    if (!!ownNickname && nickname === ownNickname) {
        return { scope: 'own', viewerUserId };
    }
    return { scope: 'profile_view', viewerUserId };
}

export interface UseSocialProfileSurfaceScopeResult {
    scope: SocialProfileSurfaceScope;
    /** False while own nickname resolves. */
    ready: boolean;
}

/**
 * Resolve viewer scope from auth plus the cached own profile.
 */
export function useSocialProfileSurfaceScope(nickname: string): UseSocialProfileSurfaceScopeResult {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const viewerUserId = useAuthStore((s) => s.userId);
    const ownProfileQuery = useSocialProfile({ enabled: isLoggedIn });

    const ownNickname = ownProfileQuery.data?.social_profile?.nickname;
    const scope = resolveSocialProfileSurfaceScope({ nickname, ownNickname, isLoggedIn, viewerUserId });
    const ready = !isLoggedIn || !viewerUserId || ownProfileQuery.isSuccess || ownProfileQuery.isError;

    return { scope, ready };
}

export const socialProfileHoldingsKeys = {
    all: ['socialProfileHoldings'] as const,
    list: (nickname: string, filter: SocialProfileHoldingsFilter, scope: SocialProfileSurfaceScope) =>
        [
            ...socialProfileHoldingsKeys.all,
            'list',
            filter,
            removeEmpty({ nickname, scope: scope.scope, viewerUserId: scope.viewerUserId }),
        ] as const,
    closedAvailability: (nickname: string, scope: SocialProfileSurfaceScope) =>
        [
            ...socialProfileHoldingsKeys.all,
            'closedAvailability',
            removeEmpty({ nickname, scope: scope.scope, viewerUserId: scope.viewerUserId }),
        ] as const,
};

async function fetchSocialProfileHoldingsPage(
    nickname: string,
    filter: SocialProfileHoldingsFilter,
    scope: SocialProfileSurfaceScope,
    cursor: string | undefined,
    signal?: AbortSignal,
    limit = DEFAULT_PAGE_SIZE,
): Promise<SocialProfileHoldingsPage> {
    if (!scope.viewerUserId) throw new Error('Profile positions require authentication');
    const response = await api.post<SocialProfilePositionsResponse>(
        'social/profile/positions',
        {
            nickname,
            limit,
            closed_positions: filter === 'closed',
            ...(cursor ? { cursor } : {}),
        },
        { signal },
    );
    const { state, event_positions, cursor: nextCursor } = response.data;
    switch (state) {
        case 'content':
            return { holdings: event_positions, cursor: nextCursor || undefined, state };
        case 'empty':
        case 'hidden':
        case 'request':
        case 'requested':
            return { holdings: [], cursor: undefined, state };
        default:
            return { holdings: [], cursor: undefined, state: 'hidden' };
    }
}

/**
 * Cursor-paginated holdings keyed by profile, filter, and viewer scope.
 */
export function useSocialProfileHoldingsInfinite(
    nickname: string,
    filter: SocialProfileHoldingsFilter,
    options?: { enabled?: boolean },
): UseInfiniteQueryResult<InfiniteData<SocialProfileHoldingsPage, string | undefined>, Error> {
    const { scope, ready } = useSocialProfileSurfaceScope(nickname);
    return useInfiniteQuery({
        queryKey: socialProfileHoldingsKeys.list(nickname, filter, scope),
        queryFn: ({ pageParam, signal }) => fetchSocialProfileHoldingsPage(nickname, filter, scope, pageParam, signal),
        initialPageParam: undefined as string | undefined,
        // Falsy cursor means end-of-list.
        getNextPageParam: (lastPage) => lastPage.cursor || undefined,
        staleTime: STALE_TIME,
        gcTime: isServer ? Infinity : GC_TIME,
        retry: 2,
        enabled: !!nickname && !!scope.viewerUserId && ready && (options?.enabled ?? true),
    });
}

/**
 * Limit-1 probe for whether the Closed filter should be shown.
 */
export function useSocialProfileClosedHoldingsAvailability(
    nickname: string,
    options?: { enabled?: boolean },
): UseQueryResult<boolean, Error> {
    const { scope, ready } = useSocialProfileSurfaceScope(nickname);
    return useQuery({
        queryKey: socialProfileHoldingsKeys.closedAvailability(nickname, scope),
        queryFn: async ({ signal }) => {
            const page = await fetchSocialProfileHoldingsPage(
                nickname,
                'closed',
                scope,
                undefined,
                signal,
                AVAILABILITY_PROBE_LIMIT,
            );
            return page.state === 'content' && (page.holdings.length > 0 || !!page.cursor);
        },
        staleTime: AVAILABILITY_STALE_TIME,
        gcTime: isServer ? Infinity : GC_TIME,
        retry: 2,
        enabled: !!nickname && !!scope.viewerUserId && ready && (options?.enabled ?? true),
    });
}

/** Merge event groups across cursor pages without losing markets from a repeated event. */
export function flattenSocialProfileHoldingsPages(
    pages: SocialProfileHoldingsPage[] | undefined,
    filter: SocialProfileHoldingsFilter,
): SocialProfileEventPosition[] {
    const latest = pages?.at(-1);
    if (!latest || !['content', 'empty'].includes(latest.state)) return [];
    const events = new Map<string, SocialProfileEventPosition>();
    for (const page of pages ?? []) {
        for (const event of page.holdings) {
            // Missing event tickers must not collapse unrelated market groups.
            const key = event.event_ticker || event.market_positions.map((market) => market.market_ticker).join('|');
            const previous = events.get(key);
            const markets = new Map(previous?.market_positions.map((market) => [market.market_ticker, market]));
            for (const market of event.market_positions) markets.set(market.market_ticker, market);
            events.set(key, { ...event, market_positions: [...markets.values()] });
        }
    }
    // Merge first so an exited position on a later page replaces its earlier open record.
    return [...events.values()].flatMap((event) => {
        const marketPositions =
            filter === 'open'
                ? event.market_positions.filter(
                      (position) => !position.is_settled && !new Decimal(position.position_fp).isZero(),
                  )
                : event.market_positions;
        return marketPositions.length ? [{ ...event, market_positions: marketPositions }] : [];
    });
}

export function resolveSocialProfileHoldingsState(
    pages: SocialProfileHoldingsPage[] | undefined,
    filter: SocialProfileHoldingsFilter,
): SurfaceState | 'unknown' {
    if (!pages?.length) return 'unknown';
    const state = pages.at(-1)?.state;
    switch (state) {
        case 'content':
        case 'empty':
            return flattenSocialProfileHoldingsPages(pages, filter).length ? 'data' : 'empty';
        case 'request':
        case 'requested':
            return state;
        default:
            return 'hidden';
    }
}
